import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showVerificationOptions, setShowVerificationOptions] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [showManualVerificationRequest, setShowManualVerificationRequest] = useState(false);
  const [manualVerificationReason, setManualVerificationReason] = useState('');
  const [submittingManualRequest, setSubmittingManualRequest] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login, currentUser, resendEmailVerification } = useAuth();
  const navigate = useNavigate();

  // Navigate to dashboard when user becomes authenticated
  useEffect(() => {
    if (currentUser) {
      setLoading(false); // Reset loading state when user is authenticated
      // Check if email is verified
      if (!currentUser.emailVerified) {
        navigate('/email-verification');
      } else if (!currentUser.hasCompletedSetup) {
        navigate('/profile-setup');
      } else {
        navigate('/dashboard');
      }
    }
  }, [currentUser, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setError('');
      setLoading(true);
      await login(email, password);
      // Don't navigate here - let the useEffect handle it when currentUser updates
    } catch (error: any) {
      console.error('Login error:', error);
      
      // Handle specific error cases
      if (error.code === 'auth/user-not-found') {
        setError('No account found with this email. Please check your email or create a new account.');
      } else if (error.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again or reset your password.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Invalid email address. Please check your email and try again.');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many failed login attempts. Please try again later.');
      } else if (error.code === 'auth/email-not-verified') {
        setError('Please verify your email before signing in. Check your inbox for a verification link.');
        setShowVerificationOptions(true);
      } else {
        setError('Failed to log in. Please try again.');
      }
      
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      setResendingVerification(true);
      setError('');
      
      // First, try to sign in to get the current user
      await login(email, password);
      
      // Then resend verification email
      await resendEmailVerification();
      
      setError('Verification email sent! Please check your inbox and spam folder.');
      setShowVerificationOptions(false);
    } catch (error: any) {
      console.error('Resend verification error:', error);
      
      if (error.code === 'auth/email-not-verified') {
        // User is signed in but not verified, so we can still resend
        try {
          await resendEmailVerification();
          setError('Verification email sent! Please check your inbox and spam folder.');
          setShowVerificationOptions(false);
        } catch (resendError) {
          setError('Failed to resend verification email. Please try again later.');
        }
      } else {
        setError('Please enter your correct email and password first, then try resending verification.');
      }
    }
    
    setResendingVerification(false);
  };

  const handleManualVerificationRequest = async () => {
    try {
      setSubmittingManualRequest(true);
      setError('');
      
      const requestManualVerification = httpsCallable(functions, 'requestManualVerification');
      const result = await requestManualVerification({ 
        email: email,
        reason: manualVerificationReason || 'Firebase verification links expired or not working'
      });
      
      if ((result.data as any).success) {
        setError('Manual verification request submitted! We\'ll review it and get back to you shortly.');
        setShowManualVerificationRequest(false);
        setShowVerificationOptions(false);
        setManualVerificationReason('');
      } else {
        setError('Failed to submit verification request. Please try again later.');
      }
    } catch (error: any) {
      console.error('Manual verification request error:', error);
      setError('Failed to submit verification request. Please try again later.');
    }
    
    setSubmittingManualRequest(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{' '}
            <Link
              to="/signup"
              className="font-medium text-indigo-600 hover:text-indigo-500"
            >
              create a new account
            </Link>
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email-address" className="sr-only">
                Email address
              </label>
              <input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="relative">
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {error && (
            <div className="text-red-600 text-sm text-center">{error}</div>
          )}

          {showVerificationOptions && !showManualVerificationRequest && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <div className="text-yellow-800 text-sm mb-3">
                <strong>Email verification required!</strong><br/>
                Your account exists but needs email verification. You can:
              </div>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={handleResendVerification}
                  disabled={resendingVerification || !email || !password}
                  className="w-full bg-yellow-600 hover:bg-yellow-700 text-white py-2 px-4 rounded-md text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {resendingVerification ? 'Sending...' : 'Resend Verification Email'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowManualVerificationRequest(true)}
                  disabled={!email}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm font-medium transition-colors disabled:opacity-50"
                >
                  Request Manual Verification
                </button>
                <div className="text-xs text-yellow-700 text-center">
                  Or try{' '}
                  <Link
                    to="/forgot-password"
                    className="font-medium text-yellow-800 hover:text-yellow-900 underline"
                  >
                    resetting your password
                  </Link>
                  {' '}if you can't access your email.
                </div>
              </div>
            </div>
          )}

          {showManualVerificationRequest && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="text-blue-800 text-sm mb-3">
                <strong>Manual Verification Request</strong><br/>
                If Firebase verification emails aren't working, we can manually verify your account.
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-blue-700 mb-1">
                    Reason for manual verification:
                  </label>
                  <textarea
                    value={manualVerificationReason}
                    onChange={(e) => setManualVerificationReason(e.target.value)}
                    placeholder="e.g., Verification links expired, emails not received, etc."
                    className="w-full px-3 py-2 text-sm border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                </div>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={handleManualVerificationRequest}
                    disabled={submittingManualRequest || !email}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm font-medium transition-colors disabled:opacity-50"
                  >
                    {submittingManualRequest ? 'Submitting...' : 'Submit Request'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowManualVerificationRequest(false);
                      setManualVerificationReason('');
                    }}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded-md text-sm font-medium transition-colors"
                  >
                    Cancel
                  </button>
                </div>
                <div className="text-xs text-blue-600 text-center">
                  We'll review your request and manually verify your account if appropriate.
                </div>
              </div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>

          <div className="text-center">
            <Link
              to="/forgot-password"
              className="font-medium text-indigo-600 hover:text-indigo-500"
            >
              Forgot your password?
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;

